import { createFileRoute, Link, useLoaderData, useRouter } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@mantine/core'
import { ChevronLeft, Plus } from 'lucide-react'
import { DataTable } from '@/components/DataTable'
import { SearchableCombobox, type ComboboxItem } from '@/components/SearchableCombobox'
import { CreateTaskModal, TaskDetailDrawer, taskColumns } from '@/components/Tasks'
import { getProjectMentionableUsers, listProjectTasks } from '@/services/taskService'
import { formatUserName } from '@/services/userService'
import { useProjectAccessLevel } from '@/hooks/useProjectAccessLevel'
import { canCreateTask } from '@/lib/taskTransitions'
import { DataTableSkeleton } from '@/components/route-shell/skeletons/DataTableSkeleton'
import { useVaultContext } from './-vault-context'
import type { MentionableUser, Task } from '@/types/task'
import '@/components/Tasks/Tasks.css'

interface ProjectTasksLoaderData {
  tasks: Task[]
  loadError: string | null
}

// Reached from the contract list's header band ("Tasks", in `-vault/ContractsView.tsx`): the vault layout
// renders a bare Outlet with no header of its own, and that band is the only in-vault surface where a
// project is unambiguously selected — which this page needs, since Tasks are project-scoped.
export const Route = createFileRoute('/_app/law/vault/tasks')({
  head: () => ({ meta: [{ title: 'Tasks · AERRIS' }] }),
  // The list is the page's one discrete fetch (ADR 0027). The loader keys on the `projectId`
  // SEARCH param — the same search the vault layout derives its context from — because a loader
  // cannot read React context; with no project selected it asks for nothing. A failure is data
  // (the page's own error copy), not a throw.
  loaderDeps: ({ search }: { search: { projectId?: string } }) => ({ projectId: search.projectId }),
  loader: async ({ deps }: { deps: { projectId?: string } }): Promise<ProjectTasksLoaderData> => {
    if (!deps.projectId) return { tasks: [], loadError: null }
    try {
      return { tasks: await listProjectTasks(deps.projectId), loadError: null }
    } catch {
      return {
        tasks: [],
        loadError: "This project's tasks could not be loaded. The server may be unreachable.",
      }
    }
  },
  pendingComponent: DataTableSkeleton,
  component: ProjectTaskListPage,
})

/**
 * The **Project Task List** — the manager-facing view of every Task in one Project, where
 * assignee-lost-access warnings surface (`aerris-client/CONTEXT.md`). Distinct from My Tasks, which is
 * self-scoped and spans projects.
 *
 * Scope comes from the vault layout's selected project, since the Project *is* the access boundary
 * for Tasks: with no project selected there is no Task list to show, only the instruction to pick one.
 *
 * A READER of the project gets the list and the detail drawer but no "New task": creating is EDITOR+
 * (plan §2), and an affordance the server would refuse is not offered.
 */
function ProjectTaskListPage() {
  const { projectId, selectedProject } = useVaultContext()
  const router = useRouter()
  // Loose like the harness needs (see tasks.tsx): this component is the route's own component,
  // so the nearest match is always this route and its loader data.
  const loaded = useLoaderData({ strict: false }) as ProjectTasksLoaderData
  // Loader seeds, component owns (ADR 0027): creates splice in and drawer edits swap in place,
  // so the list is state — re-seeded whenever the loader re-runs (project switch, drawer close).
  const [tasks, setTasks] = useState<Task[]>(loaded.tasks)
  const [loadError, setLoadError] = useState<string | null>(loaded.loadError)
  const [members, setMembers] = useState<MentionableUser[]>([])
  const [assigneeFilter, setAssigneeFilter] = useState<string | undefined>()
  const [creating, setCreating] = useState(false)
  const [openTask, setOpenTask] = useState<Task | null>(null)
  // One lookup per selected project, shared by the toolbar's create button and the form it opens.
  const canCreate = canCreateTask(useProjectAccessLevel(projectId))

  useEffect(() => {
    setTasks(loaded.tasks)
    setLoadError(loaded.loadError)
  }, [loaded])

  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    getProjectMentionableUsers(projectId).then((users) => {
      if (!cancelled) setMembers(users)
    })
    return () => {
      cancelled = true
    }
  }, [projectId])

  const nameOf = useCallback(
    (uuid: string | null) => {
      if (!uuid) return 'Unassigned'
      const member = members.find((user) => user.id === uuid)
      return member ? formatUserName(member, uuid) : `User ${uuid.substring(0, 8)}`
    },
    [members],
  )

  const columns = useMemo(() => taskColumns({ nameOf, showAssignee: true }), [nameOf])

  // The assignee filter is applied client-side: the whole project's list is already loaded, so
  // filtering locally keeps the picker instant (the endpoint's assigneeUuid filter serves the
  // contract-view panel and the chatbot, which fetch narrower slices).
  const visible = useMemo(() => {
    if (!assigneeFilter) return tasks
    if (assigneeFilter === UNASSIGNED) return tasks.filter((task) => !task.assigneeUuid)
    return tasks.filter((task) => task.assigneeUuid === assigneeFilter)
  }, [tasks, assigneeFilter])

  const assigneeItems: ComboboxItem[] = useMemo(
    () => [
      { id: UNASSIGNED, label: 'Unassigned' },
      ...members.map((user) => ({ id: user.id, label: formatUserName(user, user.id) })),
    ],
    [members],
  )

  if (!projectId) {
    return (
      <div className="task-page">
        <div className="task-empty-state">Select a project to see its tasks.</div>
      </div>
    )
  }

  return (
    <div className="task-page">
      <header className="task-page-header">
        <span className="task-page-heading">
          <h1>Tasks</h1>
          <span className="task-page-subtitle">{selectedProject?.name ?? 'This project'}</span>
        </span>
        {/* The way back to where the reader came from — the vault layout has no header to hold it. */}
        <Link
          to="/law/vault"
          search={(prev: Record<string, unknown>) => prev}
          className="task-page-back"
        >
          <ChevronLeft size={14} />
          Contracts
        </Link>
      </header>

      {loadError && <div className="task-error-state">{loadError}</div>}

      <DataTable
          columns={columns}
          data={visible}
          getRowId={(task) => task.uuid}
          onRowClick={setOpenTask}
          enableColumnVisibility
          emptyMessage="No tasks in this project yet."
          toolbarLeading={
            <div style={{ minWidth: 220 }}>
              <SearchableCombobox
                items={assigneeItems}
                selectedId={assigneeFilter}
                onSelect={(id) => setAssigneeFilter(id || undefined)}
                placeholder="Any assignee"
                searchPlaceholder="Search project members..."
                emptyMessage="No project members found"
              />
            </div>
          }
          toolbarActions={
            canCreate ? (
              <Button size="xs" leftSection={<Plus size={14} />} onClick={() => setCreating(true)}>
                New task
              </Button>
            ) : undefined
          }
        />

      {canCreate && (
        <CreateTaskModal
          projectUuid={projectId}
          opened={creating}
          onClose={() => setCreating(false)}
          onCreated={(created) => setTasks((prev) => [created, ...prev])}
        />
      )}

      <TaskDetailDrawer
        task={openTask}
        onClose={() => {
          setOpenTask(null)
          // The drawer can change status, assignee and fields — re-read via the loader
          // (invalidate-on-mutation, ADR 0027) and flow back in through the seed effect.
          void router.invalidate()
        }}
        onChanged={(updated) => {
          setTasks((prev) => prev.map((t) => (t.uuid === updated.uuid ? updated : t)))
          setOpenTask(updated)
        }}
      />
    </div>
  )
}

/** Picker id for "nobody holds this" — not a user, so it cannot collide with a uuid. */
const UNASSIGNED = 'unassigned'
